import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { useTranslation } from '../../../i18n';

import * as acts from '../../../entities/providers/actions';
import { actions as ratesActs, selectors, StepType } from '../../../entities/rates';
import * as fromProviders from '../../../entities/providers/selectors';

import { dispatchProviders } from '../../../config/countries';

import { Card } from '../../element';
import { Accordion, AccordionListItem } from '../../accordion';

import { ProviderSelect } from '../../providers/provider-select';
import { ParcelWeightSlider } from '../../providers/parcel-weight-slider';
import { ParcelWidthSlider } from '../../providers/parcel-width-slider';
import { ParcelHeightSlider } from '../../providers/parcel-height-slider';
import { ParcelLengthSlider } from '../../providers/parcel-length-slider';

const FilterContainer = styled.div`
  & .FormLabel {
    font-size: 21px;
  }
`;

const ExtendedFilters: React.FC = () => {
  const [t] = useTranslation('rates');

  return (
    <>
      <div className="mb-20">
        <ParcelWeightSlider label={t('rates.filters.weight')} />
      </div>

      <div className="mb-20">
        <ParcelWidthSlider />
      </div>

      <div className="mb-20">
        <ParcelHeightSlider />
      </div>

      <div className="mb-20">
        <ParcelLengthSlider />
      </div>
    </>
  );
};

const FAQList: React.FC = () => {
  const [t, { language }] = useTranslation('kb');
  const list = useSelector(fromProviders.getFAQList);

  const kbList: AccordionListItem[] = useMemo(() => list.map((key) => t(key, { returnObjects: true })), [
    list,
    language,
  ]);

  const notEmpty = Array.isArray(kbList) && kbList.length > 0;

  return (
    <>
      {notEmpty && (
        <Card className="is-hover-shadow">
          <Accordion list={kbList} />
        </Card>
      )}
    </>
  );
};

export const RatesFilter: React.FC = () => {
  const [t] = useTranslation('rates');

  const dispatch = useDispatch();

  const showFilters = useSelector(selectors.showFilters);
  const inboundProviders = useSelector(fromProviders.getInboundProviders);

  const { code: fromCountry, provider: fromProvider } = useSelector(fromProviders.getDispatchCountry);
  const { code: toCountry, provider: inboundProvider } = useSelector(fromProviders.getInboundCountry);

  const handleFromChange = useCallback(
    (from) => {
      dispatch(acts.setCountryAct({ from }));
      dispatch(ratesActs.setStep({ step: StepType.search }));
    },
    [inboundProvider]
  );

  const handleToChange = useCallback(
    (to) => {
      dispatch(acts.setCountryAct({ to }));
      dispatch(ratesActs.setStep({ step: StepType.search }));
    },
    [fromProvider]
  );

  return (
    <FilterContainer>
      <Card className="is-hover-shadow overflow-visible mb-20">
        <h2>{t('rates.filters.title')}</h2>

        <div className="mb-20">
          <ProviderSelect country={fromCountry} providers={dispatchProviders} onChange={handleFromChange} />
        </div>

        <div className="mb-20">
          <ProviderSelect country={toCountry} providers={inboundProviders} onChange={handleToChange} />
        </div>

        {showFilters && <ExtendedFilters />}
      </Card>

      {!showFilters && <FAQList />}
    </FilterContainer>
  );
};
